import { ClientType } from '@/shared/types/enum';
import { ModelName } from '@/shared/types/model';
import { PlanInterval } from '@/shared/types/plan';

const route = (...parts: (string | false | undefined)[]) =>
  '/' +
  parts
    .filter((part) => part)
    .map((part) => part as string)
    .map((part) => (part.startsWith('/') ? part.substring(1) : part))
    .join('/');

const resourceRoute = (resource: string, resourceOptions?: { prefix?: string; archivable?: boolean }) => {
  return {
    list: (params?: { archived?: boolean }) =>
      route(
        resourceOptions?.prefix,
        resource,
        resourceOptions?.archivable && (!params ? ':archived(archived)?' : params.archived && 'archived'),
      ),
    create: () => route(resourceOptions?.prefix, resource, 'new'),
    edit: (params?: { id?: string }) => route(resourceOptions?.prefix, resource, !params ? ':id' : params.id),
  };
};

export const routes = {
  root: () => route(''),

  home: () => route('home'),

  successStories: {
    list: () => route('success-stories'),
    view: (params?: { id?: string }) => route('success-stories', !params ? ':id' : params.id),
  },

  signUp: () => route('sign-up'),
  signIn: () => route('sign-in'),
  signInVerify: () => route('sign-in/verify'),
  signOut: () => route('sign-out'),

  trial: () => route('trial'),

  subscribe: () => route('subscribe'),

  password: {
    reset: () => route('password', 'reset'),
    confirm: (params?: { token?: string; email?: string }) =>
      route('password', 'confirm', !params ? ':token' : params.token, !params ? ':email' : params.email),
  },

  account: {
    settings: () => route('account', 'settings'),
    billing: () => route('account', 'billing'),
    subscribe: (params?: { code: string; interval: PlanInterval }) =>
      route('account', 'subscribe', !params ? ':code' : params.code, !params ? ':interval' : params.interval),
  },

  members: {
    ...resourceRoute('users', { archivable: true }),
    resendInvite: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'resend-invite'),
    resetPassword: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'reset-password'),
    enableMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'enable-mfa'),
    confirmMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'confirm-mfa'),
    disableMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'disable-mfa'),
  },

  dashboard: () => route('dashboard'),

  companies: {
    ...resourceRoute('companies', { archivable: true }),
    import: {
      upload: () => route('companies', 'import'),
      delete: (params?: { id?: string }) => route('companies', 'import', !params ? ':id' : params.id),
    },
    sync: (params?: { id?: string }) => route('companies', !params ? ':id' : params.id, 'sync'),
  },

  individuals: {
    ...resourceRoute('individuals', { archivable: true }),
    import: {
      upload: () => route('individuals', 'import'),
      delete: (params?: { id?: string }) => route('individuals', 'import', !params ? ':id' : params.id),
    },
  },

  serviceManagers: resourceRoute('service-managers'),
  associations: resourceRoute('associations'),

  services: resourceRoute('services', { archivable: true }),

  tasks: {
    ...resourceRoute('tasks'),
    copy: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'copy'),
    subTasks: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'sub-tasks'),
  },

  smartTasks: {
    ...resourceRoute('smart-tasks'),
    preview: () => route('smart-tasks', 'preview'),
    offsets: (params?: { id?: string }) => route('smart-tasks', !params ? ':id' : params.id, 'offsets'),
    subTasks: (params?: { id?: string }) => route('smart-tasks', !params ? ':id' : params.id, 'sub-tasks'),
    options: {
      types: (params?: { clientType?: ClientType }) =>
        route(
          'smart-tasks',
          'options',
          'types',
          !params ? `:clientType(${Object.values(ClientType).join('|')})` : params.clientType,
        ),
      updateFields: (params?: { clientType?: ClientType }) =>
        route(
          'smart-tasks',
          'options',
          'update-fields',
          !params ? `:clientType(${Object.values(ClientType).join('|')})` : params.clientType,
        ),
    },
  },

  recurringTasks: {
    ...resourceRoute('recurring-tasks'),
    preview: () => route('recurring-tasks', 'preview'),
    copy: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'copy'),
    offsets: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'offsets'),
    subTasks: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'sub-tasks'),
  },

  messages: resourceRoute('messages'),

  templates: {
    ...resourceRoute('templates'),
    preview: () => route('templates', 'preview'),
    search: () => route('templates', 'search'),
  },

  admin: {
    accounts: {
      ...resourceRoute('accounts', { prefix: 'admin' }),
      manage: () => route('admin', 'accounts', 'manage'),
      createSmartTasks: (params?: { id?: string }) =>
        route('admin', 'accounts', !params ? ':id' : params.id, 'create-smart-tasks'),
    },
  },

  data: {
    grid: (params?: { modelName?: ModelName; archived?: boolean }) =>
      route(
        'data',
        'grid',
        !params ? ':model' : params.modelName,
        !params ? ':archived?' : params.archived && 'archived',
      ),

    editable: (params?: { model?: ModelName }) => route('data', 'editable', !params ? ':model' : params.model),

    lookup: {
      list: (params?: { model?: ModelName }) => route('data', 'lookup', !params ? ':model' : params.model),
      item: (params?: { model?: ModelName; value?: string }) =>
        route('data', 'lookup', !params ? ':model' : params.model, !params ? ':value' : params.value),
    },

    search: () => route('data', 'search'),

    uploads: {
      get: (template = true) => route('data', 'uploads', template ? ':name' : ''),
      put: () => route('data', 'uploads', 'put'),
      delete: () => route('data', 'uploads', 'delete'),
    },
  },

  settings: {
    grid: (params?: { id?: string; view?: string }) =>
      route('settings', 'grid', !params ? ':id' : params.id, !params ? ':view?' : params.view),
  },

  unauthorised: route('unauthorised'),
  notFound: route('not-found'),
  error: route('error'),

  test: {
    frontendError: () => route('test', 'frontend-error'),
    backendError: () => route('test', 'backend-error'),
  },

  legal: {
    terms: () => route('legal', 'terms'),
    privacy: () => route('legal', 'privacy'),
    gdpr: () => route('legal', 'gdpr'),
  },
};
